import { useState, useEffect, useCallback } from "react";
import { formatPlAmount } from "../utils/formatPlAmount";
import { formatBreakupAmount } from "../utils/formatBreakupAmounts";
import { formatIsoDateRangeOrdinalMonth } from "../utils/formatIsoDateOrdinalMonth";
import type { RevenueThisMonthCardProps } from "../components/revenue_components/RevenueThisMonthCard";
import {
  getRevenueBootstrapData,
  getRevenueBootstrapError,
  getRevenueBootstrapPromise,
} from "../state/revenueBootstrapStore";
import { useRevenueBootstrap } from "./useRevenueBootstrap";

export function useRevenueThisMonth(): RevenueThisMonthCardProps & {
  amountThisWeek: string;
  amountThisMonth: string;
  subTotalThisWeek: string;
  taxTotalThisWeek: string;
  subTotalThisMonth: string;
  taxTotalThisMonth: string;
  weekBadgeLabel: string;
  loading: boolean;
  error: string | null;
  refetch: () => Promise<void>;
} {
  useRevenueBootstrap();

  const [card, setCard] = useState<RevenueThisMonthCardProps>({
    title: "Invoiced This Month",
    subtext: "—",
    amount: "—",
    amountLabel: "Collected this month",
    badgePrimary: "This Month",
    badgeSecondary: "—",
    loading: false,
  });
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [amountThisWeek, setAmountThisWeek] = useState("—");
  const [amountThisMonth, setAmountThisMonth] = useState("—");
  const [subTotalThisWeek, setSubTotalThisWeek] = useState("—");
  const [taxTotalThisWeek, setTaxTotalThisWeek] = useState("—");
  const [subTotalThisMonth, setSubTotalThisMonth] = useState("—");
  const [taxTotalThisMonth, setTaxTotalThisMonth] = useState("—");
  const [weekBadgeLabel, setWeekBadgeLabel] = useState("—");

  const syncFromBootstrap = useCallback(async () => {
    try {
      setLoading(true);
      setError(null);
      let bootstrapEntity = getRevenueBootstrapData();
      if (!bootstrapEntity) {
        const bootstrapPromise = getRevenueBootstrapPromise();
        if (bootstrapPromise) {
          bootstrapEntity = await bootstrapPromise;
        }
      }

      const bootstrapError = getRevenueBootstrapError();
      if (!bootstrapEntity) {
        throw new Error(bootstrapError ?? "Revenue bootstrap data unavailable");
      }

      const section = bootstrapEntity.sections.revenueThisMonth;
      if (section.status !== "ok" || !section.data) {
        throw new Error(section.error ?? "Revenue this month unavailable");
      }

      const entity = section.data;
      const monthLine = entity.monthLabel?.trim() || "Month unavailable";
      const rangeLine = formatIsoDateRangeOrdinalMonth(
        entity.weekStart,
        entity.weekEnd,
      );
      const periodLabel = rangeLine ?? "Week dates unavailable";
      const subtext = `${monthLine} · ${periodLabel}`;
      const badgeSecondary = `Week ${entity.weekNumberInMonth} of ${entity.totalWeeksInMonth}`;
      // Headline totals: keep API totals and existing formatting.
      const weekAmount = formatPlAmount(entity.revenueThisWeek);
      const monthAmount = formatPlAmount(entity.revenueThisMonth);

      // Breakup amounts: use round-to-decimals formatter (different from formatPlAmount truncation).
      const weekSubTotal = formatBreakupAmount(entity.subTotalThisWeek);
      const weekTaxTotal = formatBreakupAmount(entity.taxTotalThisWeek);
      const monthSubTotal = formatBreakupAmount(entity.subTotalThisMonth);
      const monthTaxTotal = formatBreakupAmount(entity.taxTotalThisMonth);
      setAmountThisWeek(weekAmount);
      setAmountThisMonth(monthAmount);
      setSubTotalThisWeek(weekSubTotal);
      setTaxTotalThisWeek(weekTaxTotal);
      setSubTotalThisMonth(monthSubTotal);
      setTaxTotalThisMonth(monthTaxTotal);
      setWeekBadgeLabel(badgeSecondary);

      setCard({
        title: "Invoiced This Month",
        subtext,
        amount: monthAmount,
        amountLabel: "Collected this month",
        badgePrimary: "This Month",
        badgeSecondary,
        showEst: false,
        loading: false,
      });
    } catch (err) {
      const message =
        err instanceof Error
          ? err.message
          : "Failed to load revenue this month";
      setError(message);
      setCard((prev) => ({
        ...prev,
        subtext: "—",
        amount: "—",
        badgeSecondary: "—",
        loading: false,
      }));
      setAmountThisWeek("—");
      setAmountThisMonth("—");
      setSubTotalThisWeek("—");
      setTaxTotalThisWeek("—");
      setSubTotalThisMonth("—");
      setTaxTotalThisMonth("—");
      setWeekBadgeLabel("—");
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    void syncFromBootstrap();
  }, [syncFromBootstrap]);

  return {
    ...card,
    amountThisWeek,
    amountThisMonth,
    subTotalThisWeek,
    taxTotalThisWeek,
    subTotalThisMonth,
    taxTotalThisMonth,
    weekBadgeLabel,
    loading,
    error,
    refetch: syncFromBootstrap,
  };
}
